import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SignaturePad from "../../components/SignaturePad";
import { driverApi } from "../../services/driverApi";

// ── Helpers ──────────────────────────────────────────────────────────────────
const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelCls = "block text-sm font-medium text-gray-700 mb-1";
const halfCls = "flex-1 min-w-0";

function Label({ children }) {
  return <label className={labelCls}>{children}</label>;
}
function Input({ value, onChange, placeholder, type = "text" }) {
  return <input type={type} value={value} onChange={onChange} placeholder={placeholder} className={inputCls} />;
}
function Row({ children }) {
  return <div className="flex gap-3 flex-wrap">{children}</div>;
}
function SectionCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h2 className="font-semibold text-gray-900 mb-0.5">{title}</h2>
      {subtitle && <p className="text-xs text-gray-400 mb-4">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function YesNo({ value, onChange, label }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-4">
        {["yes", "no"].map((v) => (
          <label key={v} className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="radio" name={label} value={v} checked={value === v} onChange={() => onChange(v)} className="accent-blue-600" />
            {v === "yes" ? "Yes" : "No"}
          </label>
        ))}
      </div>
    </div>
  );
}

const emptyEmployer = () => ({ name: "", address: "", positionHeld: "", supervisor: "", dateFrom: "", dateTo: "", salary: "", reasonsForLeaving: "", federalRegs: "no", alcoholTesting: "no" });
const emptyAccident = () => ({ date: "", nature: "", fatalities: "", injuries: "" });
const emptyConviction = () => ({ location: "", date: "", charge: "", penalty: "" });

// ── Main Component ────────────────────────────────────────────────────────────
export default function DriverApplicationForm({ driver }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [step, setStep] = useState(0); // 0=personal,1=experience,2=history,3=employment,4=sign
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [signerName, setSignerName] = useState(`${driver?.firstName || ""} ${driver?.lastName || ""}`.trim());

  // ── Form state ───────────────────────────────────────────────────────────
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US") : "";

  const [personal, setPersonal] = useState({
    firstName: driver?.firstName || "",
    middleName: "",
    maidenName: "",
    lastName: driver?.lastName || "",
    dateOfBirth: fmt(driver?.dateOfBirth),
    phone: driver?.phone || "",
    addressStreet: driver?.address?.street || "",
    addressCity: driver?.address?.city || "",
    addressState: driver?.address?.state || "CO",
    addressZip: driver?.address?.zip || "",
    addressHowLong: "",
    prevAddress1Street: "", prevAddress1City: "", prevAddress1State: "", prevAddress1Zip: "", prevAddress1HowLong: "",
    prevAddress2Street: "", prevAddress2City: "", prevAddress2State: "", prevAddress2Zip: "", prevAddress2HowLong: "",
    showPrev1: false, showPrev2: false,
  });

  const [licenses, setLicenses] = useState([{
    state: driver?.licenseState || "CO",
    number: driver?.licenseNumber || "",
    type: driver?.licenseClass || "",
    expiry: fmt(driver?.licenseExpiry),
  }]);

  const [experience, setExperience] = useState({
    exp_straight_type: "Sedan / Taxi",
    exp_straight_from: "", exp_straight_to: "", exp_straight_miles: "",
    exp_semi_type: "", exp_semi_from: "", exp_semi_to: "", exp_semi_miles: "",
    exp_two_type: "", exp_two_from: "", exp_two_to: "", exp_two_miles: "",
    exp_other_type: "", exp_other_from: "", exp_other_to: "", exp_other_miles: "",
  });

  const [accidents, setAccidents] = useState([]);
  const [noAccidents, setNoAccidents] = useState(true);
  const [convictions, setConvictions] = useState([]);
  const [noConvictions, setNoConvictions] = useState(true);
  const [everDenied, setEverDenied] = useState("no");
  const [everSuspended, setEverSuspended] = useState("no");
  const [licenseExplanation, setLicenseExplanation] = useState("");

  const [employers, setEmployers] = useState([emptyEmployer()]);

  useEffect(() => {
    driverApi.getSettings().then((s) => setSettings(s?.settings || s || {})).catch(() => {});
  }, []);

  // ── Step helpers ─────────────────────────────────────────────────────────
  const steps = ["Personal Info", "Driving Experience", "History", "Employment", "Sign"];
  const pSet = (k, v) => setPersonal((p) => ({ ...p, [k]: v }));
  const expSet = (k, v) => setExperience((p) => ({ ...p, [k]: v }));
  const updateEmp = (i, k, v) => setEmployers((a) => a.map((e, idx) => idx === i ? { ...e, [k]: v } : e));

  async function handleSign({ signatureType, signatureData }) {
    if (!signerName.trim()) { setError("Please enter your full legal name."); return; }
    setSaving(true); setError("");
    try {
      const formData = {
        ...personal,
        licenses,
        ...experience,
        accidents: noAccidents ? [] : accidents,
        convictions: noConvictions ? [] : convictions,
        everDenied, everSuspended, licenseExplanation,
        employers,
        phone: driver?.phone || personal.phone,
      };
      await driverApi.signDocument({
        documentId: "new_driver_application",
        signerName,
        signatureType,
        signatureData,
        formData,
      });
      setSuccess(true);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  if (success) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Application Signed</h2>
        <p className="text-sm text-gray-500 mb-6">Your completed Driver's Application for Employment has been signed and saved to your compliance file in Dropbox.</p>
        <button onClick={() => navigate("/enrollme/driver/sign")}
          className="bg-blue-600 text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-blue-700 transition">
          Back to Documents
        </button>
      </div>
    );
  }

  // ── Step progress bar ─────────────────────────────────────────────────────
  const ProgressBar = () => (
    <div className="flex items-center gap-1 mb-6">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <button onClick={() => i < step && setStep(i)}
            className={`text-xs font-medium px-2 py-1 rounded-full transition ${i === step ? "bg-blue-600 text-white" : i < step ? "bg-green-100 text-green-700 cursor-pointer" : "bg-gray-100 text-gray-400"}`}>
            {i < step ? "✓ " : ""}{s}
          </button>
          {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-green-300" : "bg-gray-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900">Driver's Application for Employment</h1>
        <p className="text-sm text-gray-500 mt-0.5">Federal form required by 49 CFR 391. Fill in each section — most fields are pre-filled from your profile.</p>
      </div>

      <ProgressBar />
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{error}</div>}

      {/* ── Step 0: Personal Info ── */}
      {step === 0 && (
        <div className="space-y-4">
          <SectionCard title="Applicant Information" subtitle="Pre-filled from your profile — verify and complete any missing fields.">
            <Row>
              <div className={halfCls}><Label>First Name</Label><Input value={personal.firstName} onChange={(e) => pSet("firstName", e.target.value)} /></div>
              <div className={halfCls}><Label>Middle Name (optional)</Label><Input value={personal.middleName} onChange={(e) => pSet("middleName", e.target.value)} /></div>
            </Row>
            <Row>
              <div className={halfCls}><Label>Maiden Name (if any)</Label><Input value={personal.maidenName} onChange={(e) => pSet("maidenName", e.target.value)} placeholder="If applicable" /></div>
              <div className={halfCls}><Label>Last Name</Label><Input value={personal.lastName} onChange={(e) => pSet("lastName", e.target.value)} /></div>
            </Row>
            <Row>
              <div className={halfCls}><Label>Date of Birth</Label><Input value={personal.dateOfBirth} onChange={(e) => pSet("dateOfBirth", e.target.value)} placeholder="MM/DD/YYYY" /></div>
              <div className={halfCls}><Label>Phone</Label><Input value={personal.phone} onChange={(e) => pSet("phone", e.target.value)} /></div>
            </Row>
            <Label>Current Street Address</Label><Input value={personal.addressStreet} onChange={(e) => pSet("addressStreet", e.target.value)} />
            <Row>
              <div className={halfCls}><Label>City</Label><Input value={personal.addressCity} onChange={(e) => pSet("addressCity", e.target.value)} /></div>
              <div style={{ width: 70 }}><Label>State</Label><Input value={personal.addressState} onChange={(e) => pSet("addressState", e.target.value)} /></div>
              <div style={{ width: 90 }}><Label>Zip</Label><Input value={personal.addressZip} onChange={(e) => pSet("addressZip", e.target.value)} /></div>
              <div style={{ width: 100 }}><Label>How long?</Label><Input value={personal.addressHowLong} onChange={(e) => pSet("addressHowLong", e.target.value)} placeholder="e.g. 2 yrs" /></div>
            </Row>
            {!personal.showPrev1 ? (
              <button type="button" onClick={() => pSet("showPrev1", true)} className="text-xs text-blue-600 hover:underline">+ Add previous address</button>
            ) : (
              <>
                <p className="text-xs font-medium text-gray-500">Previous Address 1</p>
                <Input value={personal.prevAddress1Street} onChange={(e) => pSet("prevAddress1Street", e.target.value)} placeholder="Street" />
                <Row>
                  <div className={halfCls}><Input value={personal.prevAddress1City} onChange={(e) => pSet("prevAddress1City", e.target.value)} placeholder="City" /></div>
                  <div style={{ width: 60 }}><Input value={personal.prevAddress1State} onChange={(e) => pSet("prevAddress1State", e.target.value)} placeholder="ST" /></div>
                  <div style={{ width: 90 }}><Input value={personal.prevAddress1Zip} onChange={(e) => pSet("prevAddress1Zip", e.target.value)} placeholder="Zip" /></div>
                  <div style={{ width: 90 }}><Input value={personal.prevAddress1HowLong} onChange={(e) => pSet("prevAddress1HowLong", e.target.value)} placeholder="How long?" /></div>
                </Row>
              </>
            )}
            {personal.showPrev1 && !personal.showPrev2 && (
              <button type="button" onClick={() => pSet("showPrev2", true)} className="text-xs text-blue-600 hover:underline">+ Add another previous address</button>
            )}
            {personal.showPrev2 && (
              <>
                <p className="text-xs font-medium text-gray-500">Previous Address 2</p>
                <Input value={personal.prevAddress2Street} onChange={(e) => pSet("prevAddress2Street", e.target.value)} placeholder="Street" />
                <Row>
                  <div className={halfCls}><Input value={personal.prevAddress2City} onChange={(e) => pSet("prevAddress2City", e.target.value)} placeholder="City" /></div>
                  <div style={{ width: 60 }}><Input value={personal.prevAddress2State} onChange={(e) => pSet("prevAddress2State", e.target.value)} placeholder="ST" /></div>
                  <div style={{ width: 90 }}><Input value={personal.prevAddress2Zip} onChange={(e) => pSet("prevAddress2Zip", e.target.value)} placeholder="Zip" /></div>
                  <div style={{ width: 90 }}><Input value={personal.prevAddress2HowLong} onChange={(e) => pSet("prevAddress2HowLong", e.target.value)} placeholder="How long?" /></div>
                </Row>
              </>
            )}
          </SectionCard>

          <SectionCard title="Driver License(s)" subtitle="Your primary license is pre-filled. Add more if you hold licenses in multiple states.">
            {licenses.map((lic, i) => (
              <div key={i} className="space-y-2 pb-3 border-b border-gray-100 last:border-0">
                <Row>
                  <div style={{ width: 70 }}><Label>State</Label><Input value={lic.state} onChange={(e) => setLicenses((a) => a.map((l, idx) => idx === i ? { ...l, state: e.target.value } : l))} /></div>
                  <div className={halfCls}><Label>License Number</Label><Input value={lic.number} onChange={(e) => setLicenses((a) => a.map((l, idx) => idx === i ? { ...l, number: e.target.value } : l))} /></div>
                </Row>
                <Row>
                  <div className={halfCls}><Label>Class / Endorsements</Label><Input value={lic.type} onChange={(e) => setLicenses((a) => a.map((l, idx) => idx === i ? { ...l, type: e.target.value } : l))} placeholder="e.g. Class D" /></div>
                  <div className={halfCls}><Label>Expiration Date</Label><Input value={lic.expiry} onChange={(e) => setLicenses((a) => a.map((l, idx) => idx === i ? { ...l, expiry: e.target.value } : l))} placeholder="MM/DD/YYYY" /></div>
                </Row>
              </div>
            ))}
            {licenses.length < 3 && (
              <button type="button" onClick={() => setLicenses((a) => [...a, { state: "", number: "", type: "", expiry: "" }])}
                className="text-xs text-blue-600 hover:underline">+ Add another license</button>
            )}
          </SectionCard>

          <button onClick={() => setStep(1)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition text-sm">
            Continue — Driving Experience →
          </button>
        </div>
      )}

      {/* ── Step 1: Driving Experience ── */}
      {step === 1 && (
        <div className="space-y-4">
          <SectionCard title="Driving Experience" subtitle="Enter approximate miles and dates for each vehicle class you've operated.">
            {[
              { label: "Straight Truck / Sedan / Taxi", prefix: "straight" },
              { label: "Tractor and Semi-Trailer", prefix: "semi" },
              { label: "Tractor — Two Trailers", prefix: "two" },
              { label: "Other", prefix: "other" },
            ].map(({ label, prefix }) => (
              <div key={prefix} className="space-y-2 pb-3 border-b border-gray-100 last:border-0">
                <p className="text-sm font-medium text-gray-700">{label}</p>
                <Row>
                  <div className={halfCls}><Label>Type of Equipment</Label>
                    <Input value={experience[`exp_${prefix}_type`]} onChange={(e) => expSet(`exp_${prefix}_type`, e.target.value)} placeholder="e.g. Sedan, Yellow Cab" />
                  </div>
                  <div style={{ width: 90 }}><Label>Approx. Miles</Label>
                    <Input value={experience[`exp_${prefix}_miles`]} onChange={(e) => expSet(`exp_${prefix}_miles`, e.target.value)} placeholder="0" />
                  </div>
                </Row>
                <Row>
                  <div className={halfCls}><Label>From (year)</Label>
                    <Input value={experience[`exp_${prefix}_from`]} onChange={(e) => expSet(`exp_${prefix}_from`, e.target.value)} placeholder="YYYY" />
                  </div>
                  <div className={halfCls}><Label>To (year)</Label>
                    <Input value={experience[`exp_${prefix}_to`]} onChange={(e) => expSet(`exp_${prefix}_to`, e.target.value)} placeholder="YYYY or Present" />
                  </div>
                </Row>
              </div>
            ))}
          </SectionCard>
          <div className="flex gap-3">
            <button onClick={() => setStep(0)} className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-xl text-sm hover:bg-gray-50 transition">← Back</button>
            <button onClick={() => setStep(2)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition text-sm">Continue — History →</button>
          </div>
        </div>
      )}

      {/* ── Step 2: Accident & Conviction History ── */}
      {step === 2 && (
        <div className="space-y-4">
          <SectionCard title="Accident Record — Past 3 Years">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={noAccidents} onChange={(e) => setNoAccidents(e.target.checked)} className="accent-blue-600 w-4 h-4" />
              <span className="font-medium text-gray-700">I have no accidents to report in the past 3 years</span>
            </label>
            {!noAccidents && (
              <div className="space-y-3 mt-2">
                {accidents.map((acc, i) => (
                  <div key={i} className="space-y-2 pb-3 border-b border-gray-100 last:border-0">
                    <p className="text-xs font-medium text-gray-500">Accident {i + 1}</p>
                    <Row>
                      <div style={{ width: 130 }}><Label>Date</Label><Input value={acc.date} onChange={(e) => setAccidents((a) => a.map((r, idx) => idx === i ? { ...r, date: e.target.value } : r))} placeholder="MM/DD/YYYY" /></div>
                      <div className={halfCls}><Label>Nature of Accident</Label><Input value={acc.nature} onChange={(e) => setAccidents((a) => a.map((r, idx) => idx === i ? { ...r, nature: e.target.value } : r))} /></div>
                    </Row>
                    <Row>
                      <div className={halfCls}><Label>Fatalities</Label><Input value={acc.fatalities} onChange={(e) => setAccidents((a) => a.map((r, idx) => idx === i ? { ...r, fatalities: e.target.value } : r))} placeholder="0" /></div>
                      <div className={halfCls}><Label>Injuries</Label><Input value={acc.injuries} onChange={(e) => setAccidents((a) => a.map((r, idx) => idx === i ? { ...r, injuries: e.target.value } : r))} placeholder="0" /></div>
                    </Row>
                  </div>
                ))}
                {accidents.length < 3 && (
                  <button type="button" onClick={() => setAccidents((a) => [...a, emptyAccident()])} className="text-xs text-blue-600 hover:underline">+ Add accident</button>
                )}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Traffic Convictions — Past 3 Years" subtitle="Other than parking violations.">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={noConvictions} onChange={(e) => setNoConvictions(e.target.checked)} className="accent-blue-600 w-4 h-4" />
              <span className="font-medium text-gray-700">I have no traffic convictions to report in the past 3 years</span>
            </label>
            {!noConvictions && (
              <div className="space-y-3 mt-2">
                {convictions.map((con, i) => (
                  <div key={i} className="space-y-2 pb-3 border-b border-gray-100 last:border-0">
                    <p className="text-xs font-medium text-gray-500">Conviction {i + 1}</p>
                    <Row>
                      <div className={halfCls}><Label>Location</Label><Input value={con.location} onChange={(e) => setConvictions((a) => a.map((r, idx) => idx === i ? { ...r, location: e.target.value } : r))} /></div>
                      <div style={{ width: 130 }}><Label>Date</Label><Input value={con.date} onChange={(e) => setConvictions((a) => a.map((r, idx) => idx === i ? { ...r, date: e.target.value } : r))} placeholder="MM/DD/YYYY" /></div>
                    </Row>
                    <Row>
                      <div className={halfCls}><Label>Charge</Label><Input value={con.charge} onChange={(e) => setConvictions((a) => a.map((r, idx) => idx === i ? { ...r, charge: e.target.value } : r))} /></div>
                      <div className={halfCls}><Label>Penalty</Label><Input value={con.penalty} onChange={(e) => setConvictions((a) => a.map((r, idx) => idx === i ? { ...r, penalty: e.target.value } : r))} /></div>
                    </Row>
                  </div>
                ))}
                {convictions.length < 3 && (
                  <button type="button" onClick={() => setConvictions((a) => [...a, emptyConviction()])} className="text-xs text-blue-600 hover:underline">+ Add conviction</button>
                )}
              </div>
            )}
          </SectionCard>

          <SectionCard title="License Questions">
            <YesNo value={everDenied} onChange={setEverDenied} label="A.  Have you ever been denied a license, permit or privilege to operate a motor vehicle?" />
            <YesNo value={everSuspended} onChange={setEverSuspended} label="B.  Has any license, permit or privilege ever been suspended, revoked or denied?" />
            {(everDenied === "yes" || everSuspended === "yes") && (
              <div>
                <Label>Please explain (attach additional sheet if necessary)</Label>
                <textarea value={licenseExplanation} onChange={(e) => setLicenseExplanation(e.target.value)} rows={3}
                  className={`${inputCls} resize-none`} placeholder="Provide details..." />
              </div>
            )}
          </SectionCard>

          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-xl text-sm hover:bg-gray-50 transition">← Back</button>
            <button onClick={() => setStep(3)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition text-sm">Continue — Employment →</button>
          </div>
        </div>
      )}

      {/* ── Step 3: Employment History ── */}
      {step === 3 && (
        <div className="space-y-4">
          <SectionCard title="Employment Record" subtitle="All employment for at least 3 years. If CDL position: last 10 years. Start with your most recent employer.">
            {employers.map((emp, i) => (
              <div key={i} className="space-y-3 pb-4 border-b border-gray-100 last:border-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">{i === 0 ? "Most Recent Employer" : `Employer ${i + 1}`}</p>
                  {i > 0 && (
                    <button type="button" onClick={() => setEmployers((a) => a.filter((_, idx) => idx !== i))}
                      className="text-xs text-red-500 hover:underline">Remove</button>
                  )}
                </div>
                <div><Label>Employer Name</Label><Input value={emp.name} onChange={(e) => updateEmp(i, "name", e.target.value)} /></div>
                <div><Label>Address</Label><Input value={emp.address} onChange={(e) => updateEmp(i, "address", e.target.value)} /></div>
                <Row>
                  <div className={halfCls}><Label>Position Held</Label><Input value={emp.positionHeld} onChange={(e) => updateEmp(i, "positionHeld", e.target.value)} /></div>
                  <div className={halfCls}><Label>Supervisor</Label><Input value={emp.supervisor} onChange={(e) => updateEmp(i, "supervisor", e.target.value)} /></div>
                </Row>
                <Row>
                  <div className={halfCls}><Label>From</Label><Input value={emp.dateFrom} onChange={(e) => updateEmp(i, "dateFrom", e.target.value)} placeholder="MM/YYYY" /></div>
                  <div className={halfCls}><Label>To</Label><Input value={emp.dateTo} onChange={(e) => updateEmp(i, "dateTo", e.target.value)} placeholder="MM/YYYY or Present" /></div>
                </Row>
                <Row>
                  <div style={{ width: 130 }}><Label>Salary</Label><Input value={emp.salary} onChange={(e) => updateEmp(i, "salary", e.target.value)} placeholder="$ / hr or yr" /></div>
                  <div className={halfCls}><Label>Reason for Leaving</Label><Input value={emp.reasonsForLeaving} onChange={(e) => updateEmp(i, "reasonsForLeaving", e.target.value)} /></div>
                </Row>
                <YesNo value={emp.federalRegs} onChange={(v) => updateEmp(i, "federalRegs", v)} label="Was this employer subject to Federal (or PUC) Motor Carrier Safety Regulations?" />
                <YesNo value={emp.alcoholTesting} onChange={(v) => updateEmp(i, "alcoholTesting", v)} label="Were you subject to controlled substance & alcohol testing under 49 CFR Parts 40/382?" />
              </div>
            ))}
            {employers.length < 5 && (
              <button type="button" onClick={() => setEmployers((a) => [...a, emptyEmployer()])}
                className="text-xs text-blue-600 hover:underline font-medium">+ Add another employer</button>
            )}
          </SectionCard>

          <div className="flex gap-3">
            <button onClick={() => setStep(2)} className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-xl text-sm hover:bg-gray-50 transition">← Back</button>
            <button onClick={() => setStep(4)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition text-sm">Review & Sign →</button>
          </div>
        </div>
      )}

      {/* ── Step 4: Sign ── */}
      {step === 4 && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-blue-800 mb-1">Ready to sign</p>
            <p className="text-sm text-blue-700">
              By signing below you certify that this application was completed by you and that all entries are true and complete to the best of your knowledge (49 CFR 391).
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="mb-4">
              <label className={labelCls}>Your Full Legal Name (as it appears on your ID)</label>
              <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} />
            </div>
            <SignaturePad onSave={handleSign} onCancel={() => setStep(3)} />
            {saving && <p className="text-sm text-center text-gray-500 mt-3">Generating your signed application…</p>}
          </div>

          <button onClick={() => setStep(3)} className="w-full border border-gray-300 text-gray-700 font-medium py-3 rounded-xl text-sm hover:bg-gray-50 transition">← Back to Employment</button>
        </div>
      )}
    </div>
  );
}
